import { useEffect } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { apiRequest } from "@/lib/query-client";
import { notificationPreference, useNotificationPreference } from "./useNotificationPreference";

const TOKEN_KEY = "@fahali_push_registration";
const VAPID_KEY = process.env.EXPO_PUBLIC_FIREBASE_VAPID_KEY || "BGHHCK3R1sjgGe1U0vNqmYD6AzMGg89jnIjeZVZr4IAV67LBtjCQOYx_NJqFDOJGvGSaY6hIJbDLvbRru47CdHE";
let generation = 0;
let cachedRegistration: { token: string; userId: string } | null = null;
export type PushRegistrationResult = "enabled" | "permission_denied" | "unsupported" | "cancelled";

/** Login restores an existing grant; only the settings action asks permission. */
export function usePushToken(userId: string | null | undefined): void {
  const { enabled, loaded } = useNotificationPreference();
  useEffect(() => {
    if (!loaded || !userId || userId.startsWith("demo-")) return;
    if (enabled) void registerPushToken(userId, false).catch(() => {});
    else void unregisterPushToken().catch(() => {});
  }, [userId, enabled, loaded]);
  useEffect(() => () => { generation += 1; }, [userId]);
}

export async function registerPushToken(userId?: string, requestPermission = true): Promise<PushRegistrationResult> {
  await notificationPreference.load();
  if (!userId || userId.startsWith("demo-") || !notificationPreference.getSnapshot().enabled) return "cancelled";
  const attempt = generation;
  const stillWanted = () => attempt === generation && notificationPreference.getSnapshot().enabled;
  let token: string;
  if (Platform.OS === "web") {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator) || typeof Notification === "undefined") return "unsupported";
    const permission = requestPermission ? await Notification.requestPermission() : Notification.permission;
    if (permission !== "granted") return "permission_denied";
    const [{ getMessaging, getToken, isSupported }, { getApps }] = await Promise.all([import("firebase/messaging"), import("@/lib/firebaseAppDeferred")]);
    if (!(await isSupported()) || !getApps().length) return "unsupported";
    if (!stillWanted()) return "cancelled";
    const serviceWorkerRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    token = await getToken(getMessaging(), { vapidKey: VAPID_KEY, serviceWorkerRegistration });
  } else {
    const { getExpoPushTokenAsync, requestPermissionsAsync, getPermissionsAsync } = await import("expo-notifications");
    const permission = requestPermission ? await requestPermissionsAsync() : await getPermissionsAsync();
    if (!permission.granted) return "permission_denied";
    token = (await getExpoPushTokenAsync()).data;
  }
  if (!token || !stillWanted()) return "cancelled";
  if (cachedRegistration?.token === token && cachedRegistration.userId === userId) return "enabled";
  // Keep the token before the request: if the server accepts it but the
  // response is lost, opt-out must still be able to remove that registration.
  await AsyncStorage.setItem(TOKEN_KEY, JSON.stringify({ token, userId }));
  if (!stillWanted()) return "cancelled";
  await apiRequest("POST", "/api/push/register-token", { token, platform: Platform.OS });
  // An opt-out while the registration request was in flight must win.
  if (!stillWanted()) {
    await apiRequest("POST", "/api/push/unregister", { token });
    return "cancelled";
  }
  cachedRegistration = { token, userId };
  return "enabled";
}

/** Persisted token makes opt-out work after a reload, before re-registration. */
export async function unregisterPushToken(): Promise<void> {
  generation += 1;
  const stored = await AsyncStorage.getItem(TOKEN_KEY);
  let token = cachedRegistration?.token;
  if (!token && stored) {
    try { token = JSON.parse(stored)?.token; } catch { /* Invalid stored token. */ }
  }
  let unregisterError: unknown;
  if (token) {
    try { await apiRequest("POST", "/api/push/unregister", { token }); }
    catch (error) { unregisterError = error; }
  }
  // Remove browser registrations created before token persistence was added.
  if (Platform.OS === "web" && typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const { deleteToken, getMessaging, isSupported } = await import("firebase/messaging");
      if (await isSupported()) await deleteToken(getMessaging());
    } catch (error) { unregisterError ??= error; }
  }
  if (unregisterError) throw new Error("In-app notifications are off. Could not confirm push removal; try again or disable notifications in your device settings.");
  cachedRegistration = null;
  await AsyncStorage.removeItem(TOKEN_KEY);
}
